/**
 * Uncle Harry's – Holistic Dentist Network Page
 * dentist-page.js  |  v1.2.0
 *
 * Architecture: dentist-page.json → modular DOM builders → injection
 * All CSS token references now use --dn- prefix to avoid Foxify conflicts.
 */

/* ── Font Loader (non-blocking) ───────────────────────────── */
(function loadFonts() {
  if (document.getElementById('dn-fonts')) return;
  const link = document.createElement('link');
  link.id   = 'dn-fonts';
  link.rel  = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=DM+Sans:wght@400;500;600;700&display=swap';
  document.head.appendChild(link);
})();

/* ── DOM Utilities ────────────────────────────────────────── */
const el = (tag, cls, attrs = {}) => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  Object.entries(attrs).forEach(([k, v]) => {
    if (k === 'text') e.textContent = v;
    else if (k === 'html') e.innerHTML = v;
    else e.setAttribute(k, v);
  });
  return e;
};

const append = (parent, ...children) => {
  children.forEach(c => c && parent.appendChild(c));
  return parent;
};

const stars = n => '★'.repeat(Math.min(5, parseInt(n) || 5)) + '☆'.repeat(Math.max(0, 5 - parseInt(n)));

// Returns null for known placeholder / broken URLs — callers render emoji instead
function safeImg(src, cls, alt = '') {
  if (!src) return null;
  const badPatterns = ['example.jpg', 'testimonial.jpg', 'partner.jpg', 'logo.png'];
  if (badPatterns.some(p => src.includes(p))) return null;
  const img = el('img', cls, { src, alt });
  img.onerror = function () { this.style.display = 'none'; };
  return img;
}


/* ══════════════════════════════════════════════════════════
   SHARED SLIDER BUILDER
   visibleCount: how many items shown at once
   items: array of DOM elements
   Returns { wrapper, updateVisible } — wrapper goes into DOM,
   updateVisible(newItems) swaps cards without rebuilding chrome.
   ══════════════════════════════════════════════════════════ */
function buildSlider(items, visibleCount) {
  const wrapper  = el('div', 'dn-slider-wrap');
  const innerRow = el('div', 'dn-slider-inner-row');  // arrows + viewport in a row
  const prevBtn  = el('button', 'dn-slider-arrow dn-slider-arrow--prev', { 'aria-label': 'Previous' });
  const nextBtn  = el('button', 'dn-slider-arrow dn-slider-arrow--next', { 'aria-label': 'Next' });
  prevBtn.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M15 18l-6-6 6-6"/></svg>';
  nextBtn.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M9 18l6-6-6-6"/></svg>';

  const viewport = el('div', 'dn-slider-viewport');
  const track    = el('div', 'dn-slider-track');
  viewport.appendChild(track);

  const dotsRow  = el('div', 'dn-slider-dots');

  let currentIndex = 0;
  let currentItems = items;

  function setup(newItems) {
    currentItems = newItems;
    currentIndex = 0;
    track.innerHTML = '';
    newItems.forEach(item => track.appendChild(item));
    buildDots();
    requestAnimationFrame(() => goTo(0));
  }

  function buildDots() {
    dotsRow.innerHTML = '';
    const visible = getVisible();
    const total = Math.max(0, currentItems.length - visible + 1);
    for (let i = 0; i < total; i++) {
      const dot = el('button', i === 0 ? 'dn-dot active' : 'dn-dot', { 'aria-label': 'Slide ' + (i+1) });
      dot.addEventListener('click', () => goTo(i));
      dotsRow.appendChild(dot);
    }
  }

  function getVisible() {
    const w = window.innerWidth;
    if (w <= 480) return 1;
    if (w <= 768) return Math.min(2, visibleCount);
    return visibleCount;
  }

  function goTo(index) {
    const visible = getVisible();
    const total = Math.max(0, currentItems.length - visible + 1);
    currentIndex = Math.max(0, Math.min(index, total - 1));

    const vpW   = viewport.offsetWidth;
    const gap   = 25;
    const cardW = Math.floor((vpW - gap * (visible - 1)) / visible);

    track.querySelectorAll(':scope > *').forEach(card => {
      card.style.flex     = '0 0 ' + cardW + 'px';
      card.style.maxWidth = cardW + 'px';
    });

    track.style.transform = 'translateX(-' + (currentIndex * (cardW + gap)) + 'px)';

    dotsRow.querySelectorAll('.dn-dot').forEach((d, i) => d.classList.toggle('active', i === currentIndex));
    prevBtn.style.opacity = currentIndex === 0 ? '.3' : '1';
    nextBtn.style.opacity = currentIndex >= total - 1 ? '.3' : '1';
    prevBtn.disabled = currentIndex === 0;
    nextBtn.disabled = currentIndex >= total - 1;
  }

  prevBtn.addEventListener('click', () => goTo(currentIndex - 1));
  nextBtn.addEventListener('click', () => goTo(currentIndex + 1));
  window.addEventListener('resize', () => { buildDots(); goTo(currentIndex); });

  // Layout: [prevBtn] [viewport] [nextBtn] stacked above [dots]
  append(innerRow, prevBtn, viewport, nextBtn);
  append(wrapper, innerRow, dotsRow);

  setup(items);
  prevBtn.style.opacity = '.3';

  return { wrapper, updateVisible: setup, goTo };
}

/* ══════════════════════════════════════════════════════════
   MAIN RENDER
   ══════════════════════════════════════════════════════════ */
function renderPage(data) {
  const root = document.getElementById('dentist-network-page');
  if (!root) return;

  root.innerHTML = '';

  [
    buildHero(data.settings || {}),
    buildFinder(data.dentists || []),
    buildTestimonials(data.testimonials || []),
    buildWhy(),
    // buildPartners(data.partners || []),
    buildCTA(data.settings || {}),
    buildFAQ(data.faqs || []),
  ].forEach(s => s && root.appendChild(s));

  initInteractions(root);
}

/* ══════════════════════════════════════════════════════════
   HERO
   ══════════════════════════════════════════════════════════ */
function buildHero(s) {
  const section = el('section', 'dn-hero');
  const wrap    = el('div', 'dn-container');
  const banner  = el('div', 'dn-hero-banner');

  // Background image — eager load for LCP
  if (s.hero_image) {
    const bg = document.createElement('img');
    bg.className = 'dn-hero-bg';
    bg.src       = s.hero_image;
    bg.alt       = '';
    bg.loading   = 'eager';
    bg.decoding  = 'async';
    bg.onerror   = () => bg.remove();
    banner.appendChild(bg);
  }

  banner.appendChild(el('div', 'dn-hero-overlay'));

  const content = el('div', 'dn-hero-content');
  const inner   = el('div', 'dn-hero-content-inner');

  // 1. Trust pills ABOVE heading (replaces eyebrow — better social proof flow)
  const trust = el('div', 'dn-hero-trust');
  const trustPoints = [
    { icon: s.hero_trust_1_icon || '🌿', text: s.hero_trust_1 || 'Natural · Fluoride-Free · Non-Toxic' },
    { icon: s.hero_trust_2_icon || '✅', text: s.hero_trust_2 || '100% Natural · No Artificial Additives' },
  ];
  trustPoints.forEach(point => {
    const pill = el('div', 'dn-trust-pill');
    const icon = el('span', 'dn-trust-pill-icon');
    icon.textContent = point.icon;
    const text = el('span', 'dn-trust-pill-text');
    text.textContent = point.text;
    append(pill, icon, text);
    trust.appendChild(pill);
  });

  // 2. Split heading — light prefix line + bold main line
  const h1 = document.createElement('h1');

  const headingFull = s.hero_heading || 'Trusted by Holistic Dentists Nationwide';
  // Split at first meaningful break: "Trusted by" + rest
  const splitAt = headingFull.indexOf(' by ');
  if (splitAt !== -1) {
    const prefix = el('span', 'dn-h1-prefix');
    prefix.textContent = headingFull.slice(0, splitAt + 4);
    const main = el('span', 'dn-h1-main');
    main.textContent = headingFull.slice(splitAt + 4);
    append(h1, prefix, main);
  } else {
    h1.textContent = headingFull;
  }

  // 3. Short subtext — one punchy line
  const sub = el('p', 'dn-hero-sub');
  sub.textContent = s.hero_subheading || 'Natural oral care, trusted by practitioners across the USA.';

  // 4. Buttons — primary dominant, secondary as text-link
  const btns = el('div', 'dn-hero-btns');

  const btnPrimary = el('a', 'dn-btn-primary', { href: '#dentist-finder' });
  btnPrimary.innerHTML = `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>`;
  btnPrimary.appendChild(document.createTextNode(' ' + (s.hero_cta_primary || 'Find a Dentist')));

  const btnSecondary = el('a', 'dn-btn-text-link', { href: '/pages/wholesale', target: '_blank', rel: 'noopener noreferrer' });
  btnSecondary.innerHTML = (s.hero_cta_secondary || 'Apply for Wholesale') + ` <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><path d="M5 12h14M12 5l7 7-7 7"/></svg>`;

  append(btns, btnPrimary, btnSecondary);

  append(inner, trust, h1, sub, btns);
  content.appendChild(inner);
  banner.appendChild(content);
  wrap.appendChild(banner);
  section.appendChild(wrap);
  return section;
}

/* ══════════════════════════════════════════════════════════
   MAPBOX CONFIG
   To use a second Mapbox account: swap the token below.
   ══════════════════════════════════════════════════════════ */
const DENTIST_MAPBOX_TOKEN = 'pk.eyJ1Ijoicml0dWthbmciLCJhIjoiY21veTY1b3h1MDlhOTJzc2pwcXkzYTVjNCJ9.UeeDNldA3UWuTLcx3n6q2A';

/* ══════════════════════════════════════════════════════════
   FINDER / DENTIST LISTING + MAPBOX
   ══════════════════════════════════════════════════════════ */
function buildFinder(dentists) {
  const section = el('section', 'dn-section dn-finder');
  section.id = 'dentist-finder';
  const wrap   = el('div', 'dn-container');
  const layout = el('div', 'dn-finder-layout');

  /* ── Left panel ── */
  const panel = el('div', 'dn-finder-panel');

  const panelTitle = el('h3');
  panelTitle.textContent = 'Find a Holistic Dentist Near You';
  const panelSub = el('p');
  panelSub.textContent = "Search by location to find dental professionals who use Uncle Harry's products.";

  const cityField = el('div', 'dn-field');
  const cityInput = el('input', 'dn-input', {
    type: 'text',
    placeholder: 'Enter your city, state or ZIP code',
    id: 'dn-city-input',
  });
  cityField.appendChild(cityInput);

  const radiusField  = el('div', 'dn-field');
  const radiusSelect = el('select', 'dn-input', { id: 'dn-radius' });
  [['Within 10 miles', 10], ['Within 25 miles', 25], ['Within 50 miles', 50], ['Within 100 miles', 100]].forEach(([text, val]) => {
    const o = document.createElement('option');
    o.value = val; o.textContent = text;
    radiusSelect.appendChild(o);
  });
  radiusSelect.value = 25;
  radiusField.appendChild(radiusSelect);

  const specFieldset = el('fieldset', 'dn-spec-fieldset');
  const specLegend = el('legend', 'dn-specialties-label');
  specLegend.textContent = 'Specialties (Optional)';
  specFieldset.appendChild(specLegend);

  const checkGroup = el('div', 'dn-checkbox-group');
  ['Holistic Dentistry', 'Biological Dentistry', 'Fluoride-Free', 'Mercury-Free Dentistry', 'Remineralization Focused'].forEach(spec => {
    const item  = el('label', 'dn-checkbox-item');
    const check = el('input', '', { type: 'checkbox', value: spec });
    const lbl   = el('span');
    lbl.textContent = spec;
    append(item, check, lbl);
    checkGroup.appendChild(item);
  });
  specFieldset.appendChild(checkGroup);

  const findMeBtn = el('button', 'dn-find-me-btn', { id: 'dn-find-me-btn' });
  findMeBtn.textContent = '📍 Find Dentists Near Me';

  // Privacy notice — shown when Find Me is clicked (HIPAA/GDPR best practice)
  const geoNotice = el('div', '', { id: 'dn-geo-notice' });
  geoNotice.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg><span>Your location is used only to find nearby dentists and is never stored.</span>`;

  const searchBtn = el('button', 'dn-search-btn', { id: 'dn-search-btn' });
  searchBtn.textContent = 'Search Dentists';

  append(panel, panelTitle, panelSub, cityField, radiusField, specFieldset, findMeBtn, geoNotice, searchBtn);

  // FTC disclaimer — dentists opted in, not paid placements
  const disclaimer = el('p', 'dn-finder-disclaimer');
  disclaimer.textContent = "Dentists listed here have opted into the Uncle Harry's professional network. Listings are not paid placements.";

  /* ── Right: real Mapbox map + cards ── */
  const mapArea  = el('div', 'dn-map-area');
  const mapDiv   = el('div', '', { id: 'dn-dentist-map' });

  const carousel = el('div', 'dn-dentist-carousel');

  if (!dentists.length) {
    const msg = el('p', 'dn-empty');
    msg.textContent = 'No dentists listed yet.';
    carousel.appendChild(msg);
  } else {
    const dentistSlider = buildSlider(
      dentists.map(d => buildDentistCard(d)),
      4  // show 4 dentist cards at a time
    );
    dentistSlider.wrapper.id = 'dn-dentist-slider';
    // expose so search/findme can update cards
    window._dnDentistSlider = dentistSlider;
    carousel.appendChild(dentistSlider.wrapper);
  }

  append(mapArea, mapDiv, carousel);
  append(layout, panel, mapArea);
  wrap.appendChild(disclaimer);
  wrap.appendChild(layout);
  section.appendChild(wrap);

  // Init Mapbox after this section is in the DOM
  requestAnimationFrame(() => initDentistMap(dentists));

  return section;
}

/* ── Mapbox initialiser ──────────────────────────────────── */
function initDentistMap(dentists) {
  if (typeof mapboxgl === 'undefined') return;

  mapboxgl.accessToken = DENTIST_MAPBOX_TOKEN;

  const map = new mapboxgl.Map({
    container: 'dn-dentist-map',
    style: 'mapbox://styles/mapbox/streets-v12',
    center: [-98.5795, 39.8283],
    zoom: 3,
    scrollZoom: false,
  });

  map.addControl(new mapboxgl.NavigationControl(), 'top-right');

  let markers   = [];
  let userMarker = null;

  function haversineKm(lat1, lon1, lat2, lon2) {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2)**2 +
      Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*Math.sin(dLon/2)**2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  }

  function renderMarkers(list) {
    markers.forEach(m => m.remove());
    markers = [];
    const bounds = new mapboxgl.LngLatBounds();
    let hasPoints = false;

    list.forEach(d => {
      const lat = parseFloat(d.lat);
      const lng = parseFloat(d.lng);
      if (isNaN(lat) || isNaN(lng)) return;

      const popup = new mapboxgl.Popup({ offset: 28 }).setHTML(
        '<div class="dn-map-popup">' +
        '<p class="dn-map-popup-name">' + (d.name||'') + '</p>' +
        '<p class="dn-map-popup-clinic">' + (d.clinic||'') + '</p>' +
        '<p class="dn-map-popup-loc">\u{1F4CD} ' + (d.city||'') + ', ' + (d.state||'') + '</p>' +
        (d.phone ? '<p class="dn-map-popup-phone">\u{1F4DE} ' + d.phone + '</p>' : '') +
        '<div class="dn-map-popup-btns">' +
        (d.website ? '<a href="' + d.website + '" target="_blank" rel="noopener" class="dn-map-popup-btn">Website</a>' : '') +
        '<a href="https://www.google.com/maps/dir/?api=1&destination=' + lat + ',' + lng + '" target="_blank" class="dn-map-popup-btn dn-map-popup-btn--primary">Directions</a>' +
        '</div></div>'
      );

      // Custom branded marker
      const markerEl = document.createElement('div');
      markerEl.className = 'dn-map-marker';
      markerEl.innerHTML = '<svg viewBox="0 0 24 32" xmlns="http://www.w3.org/2000/svg"><path d="M12 0C5.37 0 0 5.37 0 12c0 9 12 20 12 20s12-11 12-20C24 5.37 18.63 0 12 0z" fill="#2d5a3d"/><circle cx="12" cy="12" r="5" fill="white"/></svg>';

      const marker = new mapboxgl.Marker({ element: markerEl })
        .setLngLat([lng, lat])
        .setPopup(popup)
        .addTo(map);

      // Sidebar card click → fly + popup
      const card = document.querySelector('[data-dentist-id="' + d.id + '"]');
      if (card) {
        card.addEventListener('click', () => {
          map.flyTo({ center: [lng, lat], zoom: 13, duration: 1000 });
          marker.togglePopup();
          document.querySelectorAll('.dn-dentist-card').forEach(c => c.classList.remove('dn-card-active'));
          card.classList.add('dn-card-active');
        });
      }

      markers.push(marker);
      bounds.extend([lng, lat]);
      hasPoints = true;
    });

    if (hasPoints && list.length > 1) {
      map.fitBounds(bounds, { padding: 60, maxZoom: 11, duration: 1200 });
    } else if (hasPoints) {
      map.flyTo({ center: [parseFloat(list[0].lng), parseFloat(list[0].lat)], zoom: 12 });
    }
  }

  map.on('load', () => { map.resize(); renderMarkers(dentists); });

  // Expose for search button
  window._dnMapRenderMarkers = renderMarkers;
  window._dnDentists         = dentists;
  window._dnHaversineKm      = haversineKm;

  // Find Me
  const findMeBtn = document.getElementById('dn-find-me-btn');
  if (findMeBtn) {
    findMeBtn.addEventListener('click', () => {
      if (!navigator.geolocation) { alert('Geolocation not supported.'); return; }
      // Privacy notice before browser prompt fires (HIPAA/GDPR best practice)
      const notice = document.getElementById('dn-geo-notice');
      if (notice) notice.style.display = 'flex';
      findMeBtn.textContent = '📍 Finding near you...';
      navigator.geolocation.getCurrentPosition(pos => {
        const uLat = pos.coords.latitude;
        const uLng = pos.coords.longitude;
        const radiusKm = parseFloat(document.getElementById('dn-radius')?.value || 25) * 1.60934;

        const nearby = dentists.filter(d => {
          const lat = parseFloat(d.lat), lng = parseFloat(d.lng);
          return !isNaN(lat) && !isNaN(lng) && haversineKm(uLat, uLng, lat, lng) <= radiusKm;
        });

        if (userMarker) userMarker.remove();
        userMarker = new mapboxgl.Marker({ color: '#3abfad' })
          .setLngLat([uLng, uLat]).addTo(map);

        map.flyTo({ center: [uLng, uLat], zoom: 9 });
        renderMarkers(nearby);

        // Sync sidebar slider with nearby dentists
        if (window._dnDentistSlider) {
          window._dnDentistSlider.updateVisible(nearby.map(d => buildDentistCard(d)));
        }

        findMeBtn.textContent = nearby.length
          ? ('📍 ' + nearby.length + ' dentist' + (nearby.length > 1 ? 's' : '') + ' near you')
          : '📍 No dentists in range';

      }, () => {
        alert('Unable to get your location.');
        findMeBtn.textContent = '📍 Find Dentists Near Me';
      });
    });
  }
}

function buildDentistCard(d) {
  const card = el('div', 'dn-dentist-card');
  card.dataset.dentistId = d.id || '';

  // Image or placeholder
  const imgEl = safeImg(d.image, 'dn-dentist-card-img', d.name);
  if (imgEl) {
    card.appendChild(imgEl);
  } else {
    const ph = el('div', 'dn-dentist-card-img-placeholder');
    ph.textContent = '🦷';
    card.appendChild(ph);
  }

  const body = el('div', 'dn-dentist-card-body');

  // Header row: name + optional website icon
  const nameRow = el('div', 'dn-dentist-card-namerow');
  const name = document.createElement('h4');
  name.textContent = d.name || '';
  nameRow.appendChild(name);

  if (d.website) {
    const webIcon = el('a', 'dn-dentist-web-icon', {
      href: d.website,
      target: '_blank',
      rel: 'noopener',
      title: 'Visit Website',
    });
    webIcon.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>`;
    nameRow.appendChild(webIcon);
  }

  // Clinic
  const clinic = el('p', 'dn-dentist-card-clinic');
  clinic.textContent = d.clinic || '';

  // Specialty tag — always shown, fallback to Holistic Dentist
  const firstSpec = (d.specialties || '').split(',')[0].trim() || 'Holistic Dentist';
  const specTag = el('span', 'dn-specialty-tag');
  specTag.textContent = firstSpec;

  // Divider
  const divider = el('div', 'dn-dentist-card-divider');

  // Location
  const location = el('p', 'dn-dentist-meta');
  location.innerHTML = `📍 <strong>${[d.city, d.state].filter(Boolean).join(', ')}</strong>`;

  append(body, nameRow, clinic, specTag, divider, location);

  // Phone — only if present
  if (d.phone) {
    const ph = el('p', 'dn-dentist-meta');
    ph.innerHTML = `📞 <strong>${d.phone}</strong>`;
    body.appendChild(ph);
  }

  card.appendChild(body);
  return card;
}

/* ══════════════════════════════════════════════════════════
   TESTIMONIALS
   ══════════════════════════════════════════════════════════ */
function buildTestimonials(testimonials) {
  const section = el('section', 'dn-section dn-testimonials');
  const wrap    = el('div', 'dn-container');

  wrap.appendChild(buildSectionHeader("What Dentists Say About Uncle Harry's", '🌿'));

  if (!testimonials.length) {
    const msg = el('p', 'dn-empty');
    msg.textContent = 'No testimonials yet.';
    wrap.appendChild(msg);
  } else {
    const testimonialSlider = buildSlider(
      testimonials.map(t => buildTestimonialCard(t)),
      3  // show 3 testimonials at a time
    );
    wrap.appendChild(testimonialSlider.wrapper);
  }

  section.appendChild(wrap);
  return section;
}

function buildTestimonialCard(t) {
  const card = el('div', 'dn-testimonial-card');

  const qMark = el('span', 'dn-quote-mark');
  qMark.innerHTML = '&ldquo;';
  const rating = Math.min(5, parseInt(t.rating) || 5);
  const starsEl = el('div', 'dn-stars');
  starsEl.setAttribute('role', 'img');
  starsEl.setAttribute('aria-label', rating + ' out of 5 stars');
  const starsVisible = el('span', '');
  starsVisible.setAttribute('aria-hidden', 'true');
  starsVisible.textContent = stars(t.rating);
  starsEl.appendChild(starsVisible);
  const quote = el('p', 'dn-testimonial-quote');
  quote.textContent = t.quote || '';

  const author = el('div', 'dn-testimonial-author');
  const imgEl  = safeImg(t.image, 'dn-testimonial-avatar', t.name);
  if (imgEl) {
    author.appendChild(imgEl);
  } else {
    const ph = el('div', 'dn-testimonial-avatar-placeholder');
    ph.textContent = (t.name || 'D')[0].toUpperCase();
    author.appendChild(ph);
  }

  const info = el('div');
  const aName = el('div', 'dn-author-name');
  aName.textContent = t.name || '';
  const aTitle = el('div', 'dn-author-title');
  aTitle.textContent = [t.title, t.city].filter(Boolean).join(' · ');
  append(info, aName, aTitle);
  author.appendChild(info);

  append(card, qMark, starsEl, quote, author);
  return card;
}

/* ══════════════════════════════════════════════════════════
   WHY CHOOSE
   ══════════════════════════════════════════════════════════ */
function buildWhy() {
  const section = el('section', 'dn-section dn-why');
  const wrap    = el('div', 'dn-container');

  wrap.appendChild(buildSectionHeader("Why Dentists Choose Uncle Harry's", ''));

  const features = [
    { icon: '🌿', title: 'Natural Ingredients',      desc: "Clean, effective formulas made with nature's best." },
    { icon: '🦷', title: 'Fluoride-Free',             desc: 'Safe alternatives for whole-body wellness.' },
    { icon: '🛡️', title: 'Remineralization Support', desc: 'Formulas designed to strengthen enamel.' },
    { icon: '🌱', title: 'Essential Oils',            desc: 'Therapeutic-grade oils for oral health.' },
    { icon: '💚', title: 'Trusted by Professionals',  desc: 'Used and recommended by holistic dentists.' },
    { icon: '🔬', title: 'Non-Toxic & Safe',          desc: 'No harsh chemicals, no artificial additives.' },
  ];

  const grid = el('div', 'dn-why-grid');
  features.forEach(f => {
    const item  = el('div', 'dn-why-item');
    const icon  = el('div', 'dn-why-icon');
    icon.textContent = f.icon;
    const title = document.createElement('h4');
    title.textContent = f.title;
    const desc  = document.createElement('p');
    desc.textContent = f.desc;
    append(item, icon, title, desc);
    grid.appendChild(item);
  });

  wrap.appendChild(grid);
  section.appendChild(wrap);
  return section;
}

/* ══════════════════════════════════════════════════════════
   PARTNERS
   ══════════════════════════════════════════════════════════ */
function buildPartners(partners) {
  const section = el('section', 'dn-section dn-partners');
  const wrap    = el('div', 'dn-container');

  wrap.appendChild(buildSectionHeader('Featured Dental Partners', '🌿'));

  const grid = el('div', 'dn-partners-grid');

  if (!partners.length) {
    const msg = el('p', 'dn-empty');
    msg.textContent = 'No partners listed yet.';
    grid.appendChild(msg);
  } else {
    partners.forEach(p => grid.appendChild(buildPartnerCard(p)));
  }

  wrap.appendChild(grid);
  section.appendChild(wrap);
  return section;
}

function buildPartnerCard(p) {
  const card = el('div', 'dn-partner-card');
  if (p.website) {
    card.addEventListener('click', () => window.open(p.website, '_blank', 'noopener'));
  }

  const imgWrap = el('div', 'dn-partner-img-wrap');
  const imgEl   = safeImg(p.image, 'dn-partner-img', p.clinicName);
  if (imgEl) {
    imgWrap.appendChild(imgEl);
  } else {
    const ph = el('div', 'dn-partner-img-placeholder');
    ph.textContent = '🏥';
    imgWrap.appendChild(ph);
  }

  const footer = el('div', 'dn-partner-footer');
  const logoEl = safeImg(p.logo, 'dn-partner-logo', p.clinicName);
  if (logoEl) {
    footer.appendChild(logoEl);
  } else {
    const logoPh = el('div', 'dn-partner-logo-placeholder');
    logoPh.textContent = '🦷';
    footer.appendChild(logoPh);
  }

  const info = el('div', 'dn-partner-info');
  const iName = document.createElement('h4');
  iName.textContent = p.clinicName || '';
  const iCity = document.createElement('p');
  iCity.textContent = p.city || '';
  append(info, iName, iCity);
  footer.appendChild(info);

  append(card, imgWrap, footer);
  return card;
}

/* ══════════════════════════════════════════════════════════
   CTA BANNER
   ══════════════════════════════════════════════════════════ */
function buildCTA(s) {
  const section = el('section', 'dn-cta-section');
  const wrap    = el('div', 'dn-container');
  const banner  = el('div', 'dn-cta-banner');

  const imgCol = el('div', 'dn-cta-image-col');
  if (s.cta_image) {
    const img = el('img', '', { src: s.cta_image, alt: 'Dental Professional' });
    img.style.cssText = 'width:100%;height:100%;object-fit:cover;';
    img.onerror = () => {
      imgCol.style.background = 'linear-gradient(135deg,#c8e4d0 0%,#b5d9be 100%)';
      imgCol.innerHTML = '<div style="display:flex;align-items:center;justify-content:center;height:100%;font-size:48px;">🦷</div>';
    };
    imgCol.appendChild(img);
  } else {
    imgCol.style.background = 'linear-gradient(135deg,#c8e4d0 0%,#b5d9be 100%)';
    imgCol.innerHTML = '<div style="display:flex;align-items:center;justify-content:center;height:100%;font-size:48px;">🦷</div>';
  }

  const content = el('div', 'dn-cta-content');
  const sub     = el('p', 'dn-cta-sub');
  sub.textContent = 'Join Our Network';
  const h2 = document.createElement('h2');
  h2.textContent = s.wholesale_title || 'Are You a Dentist?';
  const desc = el('p', 'dn-cta-desc');
  desc.textContent = "Offer your patients the best in natural oral care. Apply for a wholesale account and use Uncle Harry's products in your practice or retail store.";

  const ctaBtns = el('div', 'dn-cta-btns');
  const applyBtn = el('a', 'dn-btn-primary', { href: 'https://uncleharrys.net/?country=US', target: '_blank', rel: 'noopener noreferrer' });
  applyBtn.textContent = 'Apply for Wholesale Account';
  const learnBtn = el('a', 'dn-btn-secondary', { href: '/pages/wholesale', target: '_blank', rel: 'noopener noreferrer' });
  learnBtn.textContent = 'Learn More';
  append(ctaBtns, applyBtn, learnBtn);

  append(content, sub, h2, desc, ctaBtns);

  const features = el('div', 'dn-cta-features');
  [
    ['🏷️', 'Exclusive wholesale pricing'],
    ['✅', 'High-quality natural products'],
    ['📦', 'Marketing support & resources'],
    ['🚀', 'Easy ordering & fast shipping'],
  ].forEach(([icon, text]) => {
    const item = el('div', 'dn-cta-feature');
    const ico  = el('div', 'dn-cta-feature-icon');
    ico.textContent = icon;
    const lbl  = el('span');
    lbl.textContent = text;
    append(item, ico, lbl);
    features.appendChild(item);
  });

  const deco = el('div', 'dn-cta-deco');
  deco.textContent = '🌿';

  append(banner, imgCol, content, features, deco);
  wrap.appendChild(banner);
  section.appendChild(wrap);
  return section;
}

/* ══════════════════════════════════════════════════════════
   FAQ
   ══════════════════════════════════════════════════════════ */
function buildFAQ(faqs) {
  const section = el('section', 'dn-section dn-faq');
  const wrap    = el('div', 'dn-container');

  wrap.appendChild(buildSectionHeader('Frequently Asked Questions', '🌿'));

  const grid = el('div', 'dn-faq-grid');

  if (!faqs.length) {
    const msg = el('p', 'dn-empty');
    msg.textContent = 'No FAQs yet.';
    grid.appendChild(msg);
  } else {
    faqs.forEach((f, i) => grid.appendChild(buildFAQItem(f, i)));
  }

  wrap.appendChild(grid);
  section.appendChild(wrap);
  return section;
}

function buildFAQItem(f, i) {
  const item = el('div', 'dn-faq-item');
  item.id    = `dn-faq-${i}`;

  const btn = el('button', 'dn-faq-question');
  btn.setAttribute('aria-expanded', 'false');

  const qText   = el('span');
  qText.textContent = f.question || '';
  const chevron = el('span', 'dn-faq-chevron');
  chevron.textContent = '▾';
  chevron.setAttribute('aria-hidden', 'true');
  append(btn, qText, chevron);

  const answer = el('div', 'dn-faq-answer');
  answer.setAttribute('role', 'region');
  const inner = el('div', 'dn-faq-answer-inner');
  inner.textContent = f.answer || '';
  answer.appendChild(inner);

  btn.addEventListener('click', () => {
    const open = item.classList.toggle('open');
    btn.setAttribute('aria-expanded', String(open));
  });

  append(item, btn, answer);
  return item;
}

/* ── Section Header Helper ────────────────────────────────── */
function buildSectionHeader(title, leafEmoji = '', subtitle = '') {
  const header = el('div', 'dn-section-header');
  const h2 = document.createElement('h2');
  h2.textContent = title;
  if (leafEmoji) {
    const leaf = el('span', 'leaf');
    leaf.textContent = ' ' + leafEmoji;
    h2.appendChild(leaf);
  }
  header.appendChild(h2);
  if (subtitle) {
    const p = document.createElement('p');
    p.textContent = subtitle;
    header.appendChild(p);
  }
  return header;
}

/* ── Interactions ─────────────────────────────────────────── */
function initInteractions(root) {
  // Map/List view toggle
  root.addEventListener('click', e => {
    const btn = e.target.closest('.dn-view-btn');
    if (!btn) return;
    root.querySelectorAll('.dn-view-btn').forEach(b => b.classList.remove('active'));
    btn.classList.add('active');
  });

  // Hero CTA smooth scroll
  root.querySelectorAll('a[href="#dentist-finder"]').forEach(a => {
    a.addEventListener('click', e => {
      e.preventDefault();
      document.getElementById('dentist-finder')?.scrollIntoView({ behavior: 'smooth' });
    });
  });

  // City/state text filter — syncs both sidebar cards AND map markers
  const searchBtn = root.querySelector('#dn-search-btn');
  if (searchBtn) {
    searchBtn.addEventListener('click', () => {
      const query = (root.querySelector('#dn-city-input')?.value || '').toLowerCase().trim();
      const cards = root.querySelectorAll('.dn-dentist-card');
      const matchedIds = [];
      let visible = 0;

      // Filter dentists and rebuild slider
      if (window._dnDentistSlider && window._dnDentists) {
        const filtered = query
          ? window._dnDentists.filter(d =>
              (d.name||'').toLowerCase().includes(query) ||
              (d.city||'').toLowerCase().includes(query) ||
              (d.state||'').toLowerCase().includes(query) ||
              (d.clinic||'').toLowerCase().includes(query)
            )
          : window._dnDentists;

        visible = filtered.length;
        matchedIds.push(...filtered.map(d => String(d.id)));

        window._dnDentistSlider.updateVisible(filtered.map(d => buildDentistCard(d)));

        if (window._dnMapRenderMarkers) {
          window._dnMapRenderMarkers(filtered);
        }
      }

      // Reset Find Me button text
      const findMeBtn = root.querySelector('#dn-find-me-btn');
      if (findMeBtn) findMeBtn.textContent = '📍 Find Dentists Near Me';

      const grid = root.querySelector('#dn-dentist-cards');
      if (grid) {
        grid.querySelector('.dn-empty-search')?.remove();
        if (visible === 0) {
          const msg = el('p', 'dn-empty dn-empty-search');
          msg.textContent = 'No dentists match your search. Try a different location.';
          grid.appendChild(msg);
        }
      }
    });
  }
}

/* ── Bootstrap ────────────────────────────────────────────── */
function bootstrap() {
  if (window.dentistPageData) {
    renderPage(window.dentistPageData);
    return;
  }

  // Show spinner while waiting for JSON
  const root = document.getElementById('dentist-network-page');
  if (root && !root.innerHTML.trim()) {
    const loading = el('div', 'dn-loading');
    loading.innerHTML = '<div class="dn-spinner"></div><p>Loading dentist network…</p>';
    root.appendChild(loading);
  }

  window.addEventListener('dentistDataLoaded', () => {
    renderPage(window.dentistPageData);
  }, { once: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrap);
} else {
  bootstrap();
}